import type {
  ScreenShareRepository,
  ScreenShareSession,
  ScreenShareSource,
  ScreenShareStartOptions,
  UseCaptureResult,
} from "./ScreenShareRepository.interface";

class ElectronScreenShareRepository implements ScreenShareRepository {
  private activeSessions: Map<string, ScreenShareSession> = new Map();

  constructor() {}

  private isElectronAPIAvailable(): boolean {
    return typeof window !== "undefined" && !!window.electronAPI;
  }

  async getSources(): Promise<ScreenShareSource[]> {
    if (!this.isElectronAPIAvailable()) {
      return [];
    }

    try {
      const result = await window.electronAPI?.getScreenSources();

      if (result.success && result.sources) {
        return result.sources;
      }

      return [];
    } catch (error) {
      console.error("❌ Error en getSources:", error);

      return [];
    }
  }

  async useCapture(
    options: ScreenShareStartOptions,
  ): Promise<UseCaptureResult> {
    if (!this.isElectronAPIAvailable()) {
      return {
        success: false,

        error: "ElectronAPI not available",
      };
    }

    try {
      const result = await window.electronAPI?.requestDisplayMedia({
        sourceId: options.sourceId,

        width: options.width || 1920,

        height: options.height || 1080,
      });

      if (result.success) {
        const session: ScreenShareSession = {
          source_id: options.sourceId,

          process_id: Date.now().toString(),

          port: 0,

          start_time: new Date().toISOString(),

          source_name: `Screen ${options.sourceId}`,
        };

        this.activeSessions.set(session.source_id, session);

        return {
          success: true,

          session,

          filePath: `mediastream://${session.source_id}`,

          message: "Capture started with native getDisplayMedia API",
        };
      }

      return {
        success: false,

        error: result.error || "Error preparing capture",
      };
    } catch (error) {
      return {
        success: false,

        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  async stopCapture(id: string): Promise<boolean> {
    try {
      const wasActive = this.activeSessions.delete(id);

      if (wasActive) {
        return true;
      }

      return false;
    } catch {
      return false;
    }
  }

  async getSessions(): Promise<ScreenShareSession[]> {
    try {
      const sessions = Array.from(this.activeSessions.values());

      return sessions;
    } catch {
      return [];
    }
  }

  async getStream(): Promise<string[]> {
    return [];
  }
}

export default ElectronScreenShareRepository;
